import moment from 'moment'
import { useState } from 'react'
import { useSelector } from 'react-redux'

import { selectIsInternalAdmin } from '../../accessors'
import ConfirmationDialog from '../../components/ReusableComponents/ConfirmationDialog'
import { useManagePaymentPermissions } from '../../hooks/useManagePaymentPermissions'
import AddCustomChargeDialog from './AddCustomChargeDialog'
import BillingRow from './BillingRow/index'
import styles from './PaymentRow.module.scss'

const Button = ({ children, handleClick }) => (
  <button
    onClick={handleClick}
    style={{
      padding: 0,
      border: 0,
      fontSize: 12,
      color: '#2764EA',
      marginTop: 2,
      marginLeft: 5,
      display: 'block',
      textDecoration: 'underline'
    }}
  >
    {children}
  </button>
)

export const PaymentRow = props => {
  const {
    paymentClearedOn,
    paymentAddedOn,
    paymentMethod,
    paymentAmount,
    paymentFriendlyName,
    paidForSubsidiary,
    paymentProcessedBy,
    processorStatus,
    handleRetry,
    paymentConfirmationCode,
    handleMarkAsPaid,
    handleMarkAsRejected,
    paymentPostedBy,
    paymentNotes,
    id,
    chargebackDate,
    chargebackBy,
    hasChargeback
  } = props

  const niceMethodNames = {
    CreditCard: 'Credit card',
    Check: 'Check',
    ACH: 'E-check'
  }

  const niceStatusNames = {
    InitialApproval: 'In process',
    Success: 'Cleared on',
    Error: 'Failed',
    Declined: 'Declined',
    ChargedBack: 'Charged back on'
  }

  const statusColors = {
    Declined: 'bad',
    InitialApproval: 'none',
    Success: 'good',
    Error: 'bad',
    ChargedBack: 'bad'
  }

  let title

  if (paymentProcessedBy === 'CreditIssued') {
    title = `Hotel credit for ${paidForSubsidiary}`
  } else if (paymentProcessedBy === 'CreditIssuedFromACH') {
    title = 'Credit applied for incoming ACH'
  } else if (paymentNotes === 'SingleUseCreditCard') {
    title = 'Single use credit card'
  } else {
    title =
      (niceMethodNames[paymentMethod] ? niceMethodNames[paymentMethod] : paymentMethod) +
      ` for ${paidForSubsidiary}`
  }

  const processDate =
    processorStatus === 'Success' ? moment(paymentClearedOn).format('MMM DD') : ''

  const [showingMarkAsPaidConfirmation, setShowingMarkAsPaidConfirmation] = useState()

  const [showingMarkAsRejectedConfirmation, setShowingMarkAsRejectedConfirmation] =
    useState()

  const [showingAddCustomChargeDialog, setShowingAddCustomChargeDialog] = useState(false)

  function handleConfirmMarkAsPaid() {
    setShowingMarkAsPaidConfirmation(false)
    handleMarkAsPaid()
  }

  function handleConfirmMarkAsRejected() {
    setShowingMarkAsRejectedConfirmation(false)
    handleMarkAsRejected()
  }

  const isInternalAdmin = useSelector(selectIsInternalAdmin)

  const { canManagePaymentStatuses } = useManagePaymentPermissions()

  return (
    <>
      {showingMarkAsPaidConfirmation && (
        <ConfirmationDialog
          title="Mark payment as paid"
          maxWidth={500}
          positiveMessage="Mark as paid"
          onReject={() => setShowingMarkAsPaidConfirmation(false)}
          onConfirm={handleConfirmMarkAsPaid}
          hasDangerousAction
        >
          <p>Double check following details before confirming your request.</p>
          <div style={{ marginTop: 10 }}>
            <p>
              <strong>Subsidiary:</strong> {paidForSubsidiary}
            </p>
            <p>
              <b>Approval code:</b> {paymentConfirmationCode}
            </p>
            <p>
              <b>Processed by:</b> {paymentProcessedBy}
            </p>
            <p>
              <b>Payment method:</b> {paymentMethod}
            </p>
          </div>
        </ConfirmationDialog>
      )}
      {showingMarkAsRejectedConfirmation && (
        <ConfirmationDialog
          title="Mark payment as declined"
          maxWidth={500}
          positiveMessage="Mark as declined"
          onReject={() => setShowingMarkAsRejectedConfirmation(false)}
          onConfirm={handleConfirmMarkAsRejected}
          hasDangerousAction
        >
          <p>Double check following details before confirming your request.</p>
          <div style={{ marginTop: 10 }}>
            <p>
              <strong>Subsidiary:</strong> {paidForSubsidiary}
            </p>
            <p>
              <b>Approval code:</b> {paymentConfirmationCode}
            </p>
            <p>
              <b>Processed by:</b> {paymentProcessedBy}
            </p>
            <p>
              <b>Payment method:</b> {paymentMethod}
            </p>
          </div>
        </ConfirmationDialog>
      )}
      {showingAddCustomChargeDialog && (
        <AddCustomChargeDialog
          onClose={() => setShowingAddCustomChargeDialog(false)}
          isForChargeback={true}
          paymentId={id}
          paymentAmount={paymentAmount}
        />
      )}
      <div className={styles.rowHolder}>
        {isInternalAdmin && (
          <div className={styles.tooltip}>
            <div>
              <span className={styles.tooltipLabel}>Payment #</span>
              <span className={styles.tooltipValue}>{id}</span>
            </div>
            <div>
              <span className={styles.tooltipLabel}>Posted by</span>
              <span className={styles.tooltipValue}>{paymentPostedBy}</span>
            </div>
            <div>
              <span className={styles.tooltipLabel}>Added on</span>
              <span className={styles.tooltipValue}>
                {moment(paymentAddedOn).format('YYYY-MM-DD')}
              </span>
            </div>
            <div>
              <span className={styles.tooltipLabel}>Cleared on</span>
              <span className={styles.tooltipValue}>
                {moment(paymentClearedOn).format('YYYY-MM-DD')}
              </span>
            </div>
            <div>
              <span className={styles.tooltipLabel}>Conf #</span>
              <span className={styles.tooltipValue}>{paymentConfirmationCode}</span>
            </div>
            <div>
              <span className={styles.tooltipLabel}>Notes</span>
              <span className={styles.tooltipValue}>{paymentNotes}</span>
            </div>
            {hasChargeback && (
              <div>
                <span className={styles.tooltipLabel}>Chargeback on</span>
                <span className={styles.tooltipValue}>
                  {moment(chargebackDate).format('YYYY-MM-DD')}
                </span>
              </div>
            )}
            {hasChargeback && (
              <div>
                <span className={styles.tooltipLabel}>Chargeback by</span>
                <span className={styles.tooltipValue}>{chargebackBy}</span>
              </div>
            )}
            {(processorStatus === 'InitialApproval' || processorStatus === 'Success') &&
              canManagePaymentStatuses && (
                <div className={styles.tooltipActions}>
                  {processorStatus === 'InitialApproval' && (
                    <>
                      <button onClick={() => setShowingMarkAsPaidConfirmation(true)}>
                        Mark as paid
                      </button>
                      <button onClick={() => setShowingMarkAsRejectedConfirmation(true)}>
                        Mark as declined
                      </button>
                    </>
                  )}
                  {processorStatus === 'Success' && (
                    <button onClick={() => setShowingAddCustomChargeDialog(true)}>
                      Add Chargeback
                    </button>
                  )}
                </div>
              )}
          </div>
        )}
        <BillingRow
          pretitle={moment(paymentAddedOn).format('MMM DD, YYYY')}
          title={title}
          titleInfo={paymentFriendlyName}
          amount={paymentAmount.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD'
          })}
          hasHoverState={false}
          amountInfo={
            <div className={styles.statusContainer}>
              {hasChargeback ? (
                <span>Chargeback</span>
              ) : (
                <span>
                  {niceStatusNames[processorStatus] || processorStatus} {processDate}
                </span>
              )}
              <div className={styles.retryButton}>
                <div>
                  {(processorStatus === 'Error' || processorStatus === 'Declined') &&
                    paymentMethod === 'CreditCard' && (
                      <Button handleClick={handleRetry}>Retry</Button>
                    )}
                </div>
              </div>
            </div>
          }
          amountStyle={
            hasChargeback
              ? statusColors.ChargedBack
              : statusColors[processorStatus] || 'none'
          }
        />
      </div>
    </>
  )
}
